import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ApplicationStateService } from './application-state.service';
import { catchError, lastValueFrom, Observable } from 'rxjs';
import { OfflineDataService } from './offline-data.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { take } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class RestService {

    constructor(private http: HttpClient,
                private applicationStateService: ApplicationStateService,
                private offlineDataService: OfflineDataService) {
    }

    /**
     * Make an asynchronous GET request.
     *
     * @param {string} url
     * @param {any} data
     * @param absolute
     * @returns {Promise<any>}
     */
    get(url: string, data?: any, absolute = false): Promise<any> {

        if (this.applicationStateService.isOfflineModeEnabled) {
            return this.offlineGet(url);
        }
        const assigenmentLink = environment.endpointUrls.assignmentLink;
        const options: any = {};
        options.withCredentials = true;
        options.headers = new HttpHeaders();
        options.headers.set('Content-Type', 'application/json');
        this.setGETParams(options, data);

        let absoluteURL = (!absolute ? this.getBaseUrl() : '') + url;
        if (url.startsWith(assigenmentLink)) {
            absoluteURL = url;
            options.withCredentials = false;
        }

        return lastValueFrom(this.http.get<any>(absoluteURL, options)
            .pipe(
                take(1), catchError(this.handleError)))
            .then(response => {
                return this.handleResponse(response);
            })
            .catch(this.handleError);
    }

    /**
     * Make an asynchronous POST request.
     *
     * @param {string} url
     * @param {any} data
     * @param {Headers} headers
     * @param responseType
     * @param absolute
     * @returns {Promise<any>}
     */
    post(url: string, data: any, headers?: Headers, responseType?: any, absolute = false): Promise<any> {

        const options: any = {};
        options.withCredentials = true;
        this.setGETParams(options);

        if (headers) {
            options.headers = headers;
            options.responseType = responseType;
        }

        return this.http.post((!absolute ? this.getBaseUrl() : '') + url, data, options)
            .toPromise()
            .then(response => {

                return this.handleResponse(response);

            })
            .catch(this.handleError);
    }

    /**
     * Make an asynchronous PATCH request.
     *
     * @param {string} url
     * @param {any} data
     * @param absolute
     * @returns {Promise<any>}
     */
    patch(url: string, data: any, absolute = false): Promise<any> {

        const options: any = {};
        options.withCredentials = true;
        this.setGETParams(options);

        return this.http.patch((!absolute ? this.getBaseUrl() : '') + url, data, options)
            .toPromise()
            .then(response => {
                return this.handleResponse(response);
            })
            .catch(this.handleError);
    }

    /**
     * Make an asynchronous DELETE request.
     *
     * @param {string} url
     * @param absolute
     * @returns {Promise<any>}
     */
    delete(url: string, absolute = false): Promise<any> {

        const options: any = {};
        options.withCredentials = true;
        this.setGETParams(options);

        return this.http.delete((!absolute ? this.getBaseUrl() : '') + url, options)
            .toPromise()
            .then(response => {
                return this.handleResponse(response);
            })
            .catch(this.handleError);
    }

    getObservable(url, options, data?): Observable<any> {
        if (this.applicationStateService.ownerId) {
            if (!data) {
                data = {};
            }

            data.ownerId = this.applicationStateService.ownerId;
        }
        if (this.applicationStateService.userBookId) {
            if (!data) {
                data = {};
            }

            data.userBookId = this.applicationStateService.userBookId;
        }

        if (data) {
            options.params = data;
        }

        if (environment.debug) {
            // console.log(url);
        }

        return this.http.get(this.getBaseUrl() + url, options);
    }

    /**
     * Get base url for all REST actions.
     *
     * @returns {string}
     */
    getBaseUrl(): string {
        return this.applicationStateService.baseUrl;
    }

    private handleError(error: any): Promise<any> {
        if (environment.debug) {
            console.error('An error occurred', error); // for demo purposes only
        }

        return Promise.reject(error.error || error.message || error);
    }

    private handleResponse(response): any {
        return response;
    }

    /**
     * Set GET-parameters on a request.
     *
     * @param {RequestOptions} options
     * @param {any} data
     */
    private setGETParams(options: any, data?: any): void {
        let params = new HttpParams();
        if (data !== undefined) {
            Object.keys(data)
                .forEach(key => {
                    params = params.append(key, data[key]);
                });
        }

        if (this.applicationStateService.ownerId) {
            params = params.append('ownerId', String(this.applicationStateService.ownerId));
        }
        if (this.applicationStateService.userBookId) {
            params = params.append('userBookId', String(this.applicationStateService.userBookId));
        }

        options.params = params;
    }

    private offlineGet(url: string): Promise<any> {
        const data = this.offlineDataService.getData(url);

        return new Promise(resolve => {
            resolve(data);
        });
    }
}
